import { isConnectivityAutoPushEnabled } from '@src/modules/system/domain/connectivitySyncPref.ts';

export type ConnectivitySyncOrchestratorDeps = {
  isSessionActive(): Promise<boolean>;
  isRemoteSyncAvailable(): Promise<boolean>;
  isCatalogSyncBusy(): boolean;
  isPublicShellPath(): boolean;
  countPendingRecords(): Promise<number>;
  probeNetworkReachable(): Promise<boolean>;
  pushPendingRecords(): Promise<{ pushedTotal: number }>;
  promptPush(count: number, onSend: () => void): void;
  notifyPushSuccess(pushedTotal: number): void;
  notifyPushError(message: string): void;
};

const ONLINE_DEBOUNCE_MS = 3000;
const PROMPT_COOLDOWN_MS = 60_000;

export function createConnectivitySyncOrchestrator(deps: ConnectivitySyncOrchestratorDeps): {
  handleConnectivitySignal(): void;
  dispose(): void;
} {
  let debounceId: ReturnType<typeof globalThis.setTimeout> | undefined;
  let running = false;
  let lastPromptAt = 0;

  const runPush = async (): Promise<void> => {
    if (running) return;
    running = true;
    try {
      const { pushedTotal } = await deps.pushPendingRecords();
      if (pushedTotal > 0) deps.notifyPushSuccess(pushedTotal);
    } catch (e) {
      deps.notifyPushError(e instanceof Error ? e.message : String(e));
    } finally {
      running = false;
    }
  };

  const evaluate = async (): Promise<void> => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    if (deps.isPublicShellPath()) return;
    if (!(await deps.isSessionActive())) return;
    if (!(await deps.isRemoteSyncAvailable())) return;
    if (deps.isCatalogSyncBusy()) return;
    if (!globalThis.navigator?.onLine) return;
    if (!(await deps.probeNetworkReachable())) return;

    const pending = await deps.countPendingRecords();
    if (pending <= 0) return;

    if (isConnectivityAutoPushEnabled()) {
      await runPush();
      return;
    }

    const now = Date.now();
    if (now - lastPromptAt < PROMPT_COOLDOWN_MS) return;
    lastPromptAt = now;
    deps.promptPush(pending, () => {
      void runPush();
    });
  };

  const handleConnectivitySignal = (): void => {
    if (debounceId !== undefined) globalThis.clearTimeout(debounceId);
    debounceId = globalThis.setTimeout(() => {
      debounceId = undefined;
      void evaluate();
    }, ONLINE_DEBOUNCE_MS);
  };

  const dispose = (): void => {
    if (debounceId !== undefined) {
      globalThis.clearTimeout(debounceId);
      debounceId = undefined;
    }
  };

  return { handleConnectivitySignal, dispose };
}
